
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Header from './components/Header';

interface Referral {
  id: string;
  referral_name: string;
  agent: string;
  customer_name_or_person_who_referred: string;
  phone_number: string;
  notes: string;
  timestamp: string;
  photo?: string;
}

// Monthly Referral Rankings Component
function MonthlyReferralRankings({ referrals }: { referrals: Referral[] }) {
  const currentMonth = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });

  // Get current month's referrals
  const currentMonthReferrals = referrals.filter((referral) => {
    const referralDate = new Date(referral.timestamp);
    const now = new Date();
    return (
      referralDate.getMonth() === now.getMonth() &&
      referralDate.getFullYear() === now.getFullYear()
    );
  });

  // Count referrals by agent
  const agentCounts = currentMonthReferrals.reduce((acc, referral) => {
    const agent = referral.agent || 'Unknown';
    acc[agent] = (acc[agent] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Create ranked list
  const rankedAgents = Object.entries(agentCounts)
    .map(([agent, count]) => ({
      name: agent,
      referrals: count,
      photo: getAgentPhoto(agent)
    }))
    .sort((a, b) => b.referrals - a.referrals);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return '🏆';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200">
        <h3 className="text-base font-bold text-gray-900">Monthly Referral Rankings</h3>
        <p className="text-xs text-gray-600 mt-1">{currentMonth}</p>
        <p className="text-xs text-blue-600 font-medium mt-1">
          Showing {rankedAgents.length} agents
        </p>
      </div>

      {/* List Items */}
      <div className="divide-y divide-gray-200">
        {rankedAgents.map((agent, index) => {
          const rank = index + 1;
          const isTopThree = rank <= 3;
          const rankIcon = getRankIcon(rank);

          return (
            <div
              key={index}
              className={`px-4 py-3 ${isTopThree ? 'bg-yellow-50' : 'bg-white'}`}
            >
              {/* Agent Info Row */}
              <div className="flex items-center mb-2">
                {/* Rank Badge */}
                <div className="flex-shrink-0 mr-2">
                  {rankIcon ? (
                    <span className="text-xl">{rankIcon}</span>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-blue-600">{rank}</span>
                    </div>
                  )}
                </div>

                {/* Avatar */}
                <div className="flex-shrink-0 mr-3">
                  <img
                    className="h-10 w-10 rounded-full object-cover"
                    src={agent.photo || 'https://ui-avatars.com/api/?name=Agent&background=3b82f6&color=ffffff&size=128'}
                    alt={agent.name}
                  />
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {agent.name}
                  </div>
                </div>

                {/* Referral Count */}
                <div className="flex items-center">
                  <i className="ri-user-add-fill text-blue-500 mr-1"></i>
                  <span className="text-sm font-bold text-gray-900">{agent.referrals}</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${
                      rank === 1 ? 'bg-yellow-400' :
                      rank === 2 ? 'bg-gray-400' :
                      rank === 3 ? 'bg-orange-400' :
                      'bg-blue-400'
                    }`}
                    style={{
                      width: `${(agent.referrals / Math.max(...rankedAgents.map((a) => a.referrals), 1)) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Helper to get agent photo (same mapping as in original code)
function getAgentPhoto(agent: string): string | undefined {
  const agentPhotos: { [key: string]: string } = {
    'AJ': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/f2329cfbcf33ae9b0f2d8a91c6da9b69.png',
    'Adrian': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/1cfddd0c323faee2e871d38fa787a874.png',
    'Alex': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/9506a91e2b948a80a95df4f717459f04.png',
    'Fernando': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/ca3a7c70ce6454b10b56db8c687bdb75.png',
    'Jeff': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/4992c88e639f7fa3308eca513522c5b1.png',
    'Jenny': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/38a681727cc72d08123d7abd82cb55f3.png',
    'Jesse': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/7d761e0dd331f2381c412ba047eb3c0a.png',
    'Paul': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/09fe48f131ff9b7d3d07da969b285d32.png',
    'Shan': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/96dcddae3106e07139a385ae3570b561.png',
    'Steven': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/958a53a03973a40f1fd7d402c4e31057.png',
    'Yesenia': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/6952c9d71aed10b00d0b21770a6d2ee4.png',
    'Yulisa': 'https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/2bde41cba6dac6240f8e8c37ff03ce3a.png'
  };
  return agentPhotos[agent];
}

export default function ReferralsPage() {
  const { user } = useAuth();

  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedViewAgent, setSelectedViewAgent] = useState<string>('all');
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>('all');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  useEffect(() => {
    const loadReferrals = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError('');

        const { data, error: fetchError } = await supabase
          .from('Referral Sheet')
          .select('*')
          .order('timestamp', { ascending: false });

        if (fetchError) {
          throw new Error(JSON.stringify(fetchError));
        }

        const uniqueAgents = [...new Set(data?.map(r => r.agent) || [])];
        // Mapping rows to Referral objects
        const mappedReferrals = (data || []).map((referral: any) => ({
          id: referral.whalesync_postgres_id || referral.id,
          referral_name: referral.referral_name || '',
          agent: referral.agent || 'Unknown',
          customer_name_or_person_who_referred: referral.customer_name_or_person_who_referred || '',
          phone_number: referral.phone_number || '',
          notes: referral.notes || '',
          timestamp: referral.timestamp || '',
          photo: getAgentPhoto(referral.agent) || undefined
        }));

        setReferrals(mappedReferrals);
      } catch (err: any) {
        console.error('Error loading referrals:', err);
        setError(err?.message || 'Failed to load referrals. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadReferrals();
  }, [user]);

  // Get unique agents for dropdowns
  const uniqueAgents = [...new Set(referrals.map(r => r.agent))].sort();

  // Filter referrals based on selected filters
  const filteredReferrals = referrals.filter(referral => {
    // Guard against invalid entries
    if (!referral.agent || referral.agent === 'Unknown' || referral.agent === 'N/A') {
      return false;
    }

    // Guard against invalid dates
    const referralDate = new Date(referral.timestamp);
    if (isNaN(referralDate.getTime()) || referralDate.getFullYear() < 2020) {
      return false;
    }

    // View Agent filter
    if (selectedViewAgent !== 'all' && referral.agent !== selectedViewAgent) {
      return false;
    }

    // Date filter
    const now = new Date();

    if (selectedDateFilter === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (referralDate < today) return false;
    } else if (selectedDateFilter === 'week') {
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      if (referralDate < startOfWeek) return false;
    } else if (selectedDateFilter === 'month') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      startOfMonth.setHours(0, 0, 0, 0);
      if (referralDate < startOfMonth) return false;
    } else if (selectedDateFilter === 'custom') {
      if (customStartDate) {
        const startDate = new Date(customStartDate);
        startDate.setHours(0, 0, 0, 0);
        if (referralDate < startDate) return false;
      }
      if (customEndDate) {
        const endDate = new Date(customEndDate);
        endDate.setHours(23, 59, 59, 999);
        if (referralDate > endDate) return false;
      }
    }

    return true;
  });

  // Monthly rankings calculation (kept for possible reuse)
  const monthlyRankings = (() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthlyReferrals = referrals.filter(r => {
      const referralDate = new Date(r.timestamp);
      return referralDate >= firstDayOfMonth;
    });
    const agentCounts: { [key: string]: number } = {};
    monthlyReferrals.forEach(r => {
      const agent = r.agent || 'Unknown';
      agentCounts[agent] = (agentCounts[agent] || 0) + 1;
    });
    const rankings = Object.entries(agentCounts)
      .map(([agent, count]) => ({
        agent,
        count,
        photo: getAgentPhoto(agent)
      }))
      .sort((a, b) => b.count - a.count);
    return rankings;
  })();

  const handleRefresh = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading referrals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20">
      <Header onRefresh={handleRefresh} />

      <div className="pt-16 px-4 pb-6">
        <div className="max-w-md mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Referrals</h1>

          {/* Monthly Referral Rankings */}
          <MonthlyReferralRankings referrals={referrals} />

          {/* View Filters Section */}
          <div className="bg-white rounded-xl shadow-md p-4 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">View Filters</h2>
            <div className="space-y-4">
              {/* View by Agent */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-user-line mr-1"></i>
                  Filter by Agent
                </label>
                <select
                  value={selectedViewAgent}
                  onChange={(e) => setSelectedViewAgent(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Agents ({referrals.filter(r => r.agent && r.agent !== 'Unknown' && r.agent !== 'N/A').length})</option>
                  {uniqueAgents.map((agent) => (
                    <option key={agent} value={agent}>
                      {agent} ({referrals.filter(r => r.agent === agent).length})
                    </option>
                  ))}
                </select>
              </div>

              {/* View by Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-calendar-line mr-1"></i>
                  Filter by Date
                </label>
                <select
                  value={selectedDateFilter}
                  onChange={(e) => setSelectedDateFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Dates</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {/* Custom Date Range */}
              {selectedDateFilter === 'custom' && (
                <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-blue-200">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Active Filters Summary */}
              {(selectedViewAgent !== 'all' || selectedDateFilter !== 'all') && (
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-blue-600">{filteredReferrals.length}</span> referrals found
                    </p>
                    <button
                      onClick={() => {
                        setSelectedViewAgent('all');
                        setSelectedDateFilter('all');
                        setCustomStartDate('');
                        setCustomEndDate('');
                      }}
                      className="text-xs text-blue-600 hover:text-blue-7

 700 font-medium"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Referrals List */}
          <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden">
            {filteredReferrals.length === 0 ? (
              <div className="p-12 text-center">
                <i className="ri-inbox-line text-gray-400 text-5xl mb-4"></i>
                <p className="text-gray-500 text-lg">No referrals found</p>
                <p className="text-gray-400 text-sm mt-2">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredReferrals.map((referral) => (
                  <div key={referral.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start space-x-4">
                      {/* Agent Photo */}
                      <div className="flex-shrink-0">
                        {referral.photo ? (
                          <img
                            src={referral.photo}
                            alt={referral.agent}
                            className="w-12 h-12 rounded-full object-cover border-2 border-blue-200"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center border-2 border-blue-200">
                            <i className="ri-user-line text-blue-600 text-xl"></i>
                          </div>
                        )}
                      </div>

                      {/* Referral Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {referral.referral_name}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="font-medium">Agent:</span> {referral.agent}
                            </p>
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-sm text-gray-500">
                              {new Date(referral.timestamp).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(referral.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                          {referral.customer_name_or_person_who_referred && (
                            <div className="flex items-center text-sm text-gray-600">
                              <i className="ri-user-heart-line text-blue-500 mr-2"></i>
                              <span className="font-medium mr-1">Referred by:</span>
                              {referral.customer_name_or_person_who_referred}
                            </div>
                          )}

                          {referral.phone_number && (
                            <div className="flex items-center text-sm text-gray-600">
                              <i className="ri-phone-line text-green-500 mr-2"></i>
                              <span className="font-medium mr-1">Phone:</span>
                              <a href={`tel:${referral.phone_number}`} className="text-blue-600 hover:underline">
                                {referral.phone_number}
                              </a>
                            </div>
                          )}
                        </div>

                        {referral.notes && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-700">
                              <span className="font-medium text-gray-900">Notes:</span> {referral.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <footer className="bg-white border-t border-gray-200 py-4 mt-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="text-sm text-gray-500">
                © 2013 Driven Insurance Services. All rights reserved.
              </p>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
